import React, { memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import configServ from "../../services/config";
import Swal from "sweetalert2";
import CardHeadMenu from "../Card/CardHeadMenu/CardHeadMenu";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import CheckAndReturn from "../../utility/CheckAndReturn";
import { triggerLoader } from "../../redux/reducers/LoaderTrigger";
import { handleLabel } from "../../utility/Common";
import validator from "validator";

const EnquiryCard = ({ isChanged, editData, setEditData }) => {
  //#region code
  const dispatch = useDispatch();
  const { user_id, company_id } = useSelector((state) => state.GlobalVariables);

  const initialFormData = {
    name: "",
    email: "",
    phone: "",
    age: "",
    gender: "male",
    height: "",
    weight: "",
    status: true,
    is_customer: false,
  };
  const [formData, setFormData] = useState(initialFormData);
  const [validationErrors, setValidationErrors] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isCreate, setIsCreate] = useState(false);

  useEffect(() => {
    if (editData !== null) {
      setFormData(editData);
      setIsEdit(true);
      setIsCreate(true);
    } else {
      setIsEdit(false);
    }
  }, [editData]);

  const handlePopup = () => {
    try {
      if (isCreate === true) {
        handleReset();
      }
      setIsCreate(!isCreate);
    } catch (error) {
      console.log(error);
    }
  };

  const handleChange = (e) => {
    try {
      const { name, value, type, checked } = e.target;

      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));

      if (validationErrors[name]) {
        setValidationErrors((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    } catch (e) {
      console.log(e);
    }
  };

  const validateForm = async () => {
    try {
      let errors = {};
      if (CheckAndReturn.checkError(formData.name)) {
        errors.name = "Required";
      }
      if (CheckAndReturn.checkError(formData.email)) {
        errors.email = "Required";
      } else if (!validator.isEmail(formData.email)) {
        errors.email = "Invalid format";
      }
      if (CheckAndReturn.checkError(formData.age)) {
        errors.age = "Required";
      }
      if (CheckAndReturn.checkError(formData.gender)) {
        errors.gender = "Required";
      }
      setValidationErrors(errors);
      return Object.values(errors).every((error) => error === "");
    } catch (e) {
      console.log(e);
    }
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();

      dispatch(triggerLoader());
      const isValid = await validateForm();
      if (isValid === false) {
        return false;
      }
      const dataToSend = {
        ...formData,
        company_id: company_id,
        user_id: user_id,
      };
      if (!isEdit) {
        const res = await configServ.createEnquiry(dataToSend);
        if (res.status === 200) {
          isChanged();
          handleReset();
          Swal.fire({
            icon: "success",
            title: "Success",
            text: res.message || "Enquiry created successfully.",
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to create Enquiry.",
          });
        }
      } else {
        const res = await configServ.updateEnquiry(dataToSend);
        if (res.status === 200) {
          isChanged();
          handleReset();
          Swal.fire({
            icon: "success",
            title: "Success",
            text: res.message || "Enquiry updated successfully.",
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to update Enquiry.",
          });
        }
      }
    } catch (e) {
      console.log(e);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An unexpected error occurred.",
      });
    } finally {
      dispatch(triggerLoader());
    }
  };

  const handleReset = () => {
    try {
      setFormData(initialFormData);
      setValidationErrors({});
      setEditData(null);
    } catch (e) {
      console.log(e);
    }
  };

  //#endregion

  return (
    <>
      <CardHeadMenu
        prefix={"Na"}
        suffix={"Na"}
        title={"member enquiries"}
        handlePopup={handlePopup}
        isEdit={isEdit}
        create={isCreate}
        showCreate={false}
      />

      <Box width={"100%"} sx={{ my: 2 }} display={isCreate ? "block" : "none"}>
        <form onSubmit={handleSubmit} onReset={handleReset}>
          <Grid
            container
            alignItems="flex-end"
            spacing={1}
            bgcolor={"#e3f3fc"}
            p={3}
            borderRadius={3}
          >
            <Grid container item justifyContent={"flex-start"}>
              <Typography variant="body1" fontWeight={"bold"}>
                Customer Enquiry
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label={handleLabel("Name")}
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                sx={{ background: "white" }}
                error={!!validationErrors.name}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label={handleLabel("Email")}
                type="text"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                sx={{ background: "white" }}
                error={!!validationErrors.email}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Phone"
                type="number"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                sx={{ background: "white" }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label={handleLabel("Age")}
                type="number"
                id="age"
                name="age"
                value={formData.age}
                onChange={handleChange}
                sx={{ background: "white" }}
                error={!!validationErrors.age}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel size="small">{handleLabel("Gender")}</InputLabel>
                <Select
                  fullWidth
                  size="small"
                  label={handleLabel("Gender")}
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  error={validationErrors.gender}
                  sx={{ background: "white" }}
                >
                  <MenuItem key={"male"} value={"male"}>
                    Male
                  </MenuItem>
                  <MenuItem key={"female"} value={"female"}>
                    Female
                  </MenuItem>
                  <MenuItem key={"transgender"} value={"transgender"}>
                    Transgender
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Height(cm)"
                type="number"
                id="height"
                name="height"
                value={formData.height}
                onChange={handleChange}
                sx={{ background: "white" }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Weight(kg)"
                type="number"
                id="weight"
                name="weight"
                value={formData.weight}
                onChange={handleChange}
                sx={{ background: "white" }}
              />
            </Grid>
            <Grid container justifyContent="flex-end" mt={2} spacing={1}>
              <Grid container item xs={4} md={3} justifyContent={"flex-start"}>
                <FormControlLabel
                  name="status"
                  control={<Checkbox checked={Boolean(formData.status)} />}
                  onChange={handleChange}
                  label="Hide/Unhide"
                />
              </Grid>
              <Grid container item xs={8} md={3} justifyContent={"flex-start"}>
                <FormControlLabel
                  disabled
                  name="is_customer"
                  control={<Checkbox checked={Boolean(formData.is_customer)} />}
                  onChange={handleChange}
                  label="Is Customer"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <Button
                  type="reset"
                  fullWidth
                  size="small"
                  color={"secondary"}
                  variant="contained"
                  title={"Reset"}
                  startIcon={<RotateLeftIcon />}
                >
                  {"Reset"}
                </Button>
              </Grid>
              <Grid item xs={6} md={3}>
                <Button
                  type="submit"
                  fullWidth
                  size="small"
                  variant="contained"
                  color={isEdit ? "success" : "primary"}
                  startIcon={isEdit ? <UpdateIcon /> : <SaveIcon />}
                >
                  {isEdit ? "Update" : "Save"}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Box>
    </>
  );
};

export default memo(EnquiryCard);
